import type { ApiWire } from '@/domain/shared/types';
import type { ClientIntegrationRowDto, ProviderAccountDto } from '@/services/types';

import type {
    ClientIntegrationRow,
    ProviderAccount,
    SaveProviderAccountInput,
} from './accountTypes';
import {
    credentialFlags,
    dateOrNull,
    fieldPatches,
    fieldValuesFromDto,
    secretsPatchToDto,
} from './fieldValues';
import { mapFieldSchemas, mapStoredExtra, mapUnreadable } from './mappers';
import type { SaveProviderAccountRequestWire } from './secretRequests';

function mapAccount(dto: ApiWire<ProviderAccountDto>): ProviderAccount {
    return {
        rowId: dto.rowId ?? null,
        providerName: dto.providerName,
        // The generated type drops `nullable`, so an absent key and null both mean none.
        displayKey: dto.displayKey ?? null,
        supported: dto.supported,
        authType: dto.authType,
        isActive: dto.isActive,
        updatedAt: dto.updatedAt ?? null,
        schemaFields: mapFieldSchemas(dto.schemaFields),
        fields: fieldValuesFromDto(dto.fields),
        credentials: credentialFlags(dto.credentials),
        storedExtras: (dto.storedExtras ?? []).map(mapStoredExtra),
        unreadable: (dto.unreadable ?? []).map(mapUnreadable),
    };
}

export function mapAccounts(dtos: readonly ApiWire<ProviderAccountDto>[]): ProviderAccount[] {
    return dtos.map(mapAccount);
}

/** Without `expectedUpdatedAt` the server creates the row. */
export function toAccountRequest(input: SaveProviderAccountInput): SaveProviderAccountRequestWire {
    const secrets = secretsPatchToDto(input.secrets);
    return {
        ...(input.expectedUpdatedAt !== null && { expectedUpdatedAt: input.expectedUpdatedAt }),
        ...(input.isActive !== undefined && { isActive: input.isActive }),
        fields: fieldPatches(input.fields),
        ...(secrets && { secrets }),
    };
}

export function mapIntegrations(
    dtos: readonly ApiWire<ClientIntegrationRowDto>[]
): ClientIntegrationRow[] {
    return dtos.map((dto) => ({
        rowId: dto.rowId,
        locationId: dto.locationId,
        locationName: dto.locationName,
        cageId: dto.cageId,
        cageName: dto.cageName,
        providerName: dto.providerName,
        isActive: dto.isActive,
        lastFetchedAt: dateOrNull(dto.lastFetchedAt),
    }));
}
